/**
 * TeamRoleSection Component
 * Displays team members for a specific role with add/remove functionality
 * Extracted from ProjectDetailPage for reusability
 */

import React from 'react';

// Helper to get nickname (fallback to fullName)
const getNickname = (user) => {
  if (!user) return 'N/A';
  return user.nickname || user.fullName || 'Unknown';
};

const TeamRoleSection = ({
  title,
  roleType,
  members,
  color,
  description,
  canManage,
  onAdd,
  onRemove,
  onReplace,
  removingId,
  replacingId,
  compact = false,
  stageState = 'unlocked', // 'locked' | 'unlocked' | 'completed'
  lockedMessage = null,
  showStatusBadge = false
}) => {
  const colorClasses = {
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      badge: 'bg-orange-100 text-orange-700',
      icon: 'text-orange-500'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-700',
      icon: 'text-blue-500'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-700',
      badge: 'bg-purple-100 text-purple-700',
      icon: 'text-purple-500'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      badge: 'bg-green-100 text-green-700',
      icon: 'text-green-500'
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  // Locked state - show lock icon and message
  if (stageState === 'locked') {
    return (
      <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span className="text-sm font-medium text-gray-500">{title}</span>
        </div>
        <p className="text-xs text-gray-400 mt-1 ml-6">
          {lockedMessage || 'Available at a later stage'}
        </p>
      </div>
    );
  }

  // Compact mode for inline display
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className={`text-xs font-medium ${colors.text}`}>{title}:</span>
        {members.length === 0 ? (
          <span className="text-xs text-gray-400 italic">None</span>
        ) : (
          <div className="flex items-center gap-1">
            {members.map(member => (
              <span
                key={member.id}
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}
              >
                {getNickname(member.user)}
              </span>
            ))}
          </div>
        )}
        {canManage && (
          <button
            onClick={onAdd}
            className={`text-xs px-1.5 py-0.5 rounded ${colors.badge} hover:opacity-80`}
          >
            +
          </button>
        )}
      </div>
    );
  }

  // Get status badge based on stage state
  const getStatusBadge = () => {
    if (!showStatusBadge) return null;
    if (stageState === 'completed') {
      return (
        <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700 flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Done
        </span>
      );
    }
    return null;
  };

  // Full mode (original layout)
  return (
    <div className={`p-3 rounded-lg border ${colors.border} ${colors.bg} ${stageState === 'completed' ? 'opacity-75' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <h4 className={`text-sm font-medium ${colors.text}`}>{title}</h4>
          <span className={`text-xs px-1.5 py-0.5 rounded ${colors.badge}`}>
            {members.length}
          </span>
          {getStatusBadge()}
        </div>
        {canManage && stageState !== 'completed' && (
          <button
            onClick={onAdd}
            className={`text-xs px-2 py-1 rounded transition-colors ${colors.badge} hover:opacity-80`}
          >
            + Add
          </button>
        )}
      </div>

      {description && members.length === 0 && (
        <p className="text-xs text-gray-500 mb-2">{description}</p>
      )}

      {members.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No {title.toLowerCase()} assigned</p>
      ) : (
        <div className="space-y-2">
          {members.map(member => (
            <div key={member.id} className="flex items-center justify-between bg-white rounded p-2 border border-gray-100">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-gray-900">
                  {getNickname(member.user)}
                </p>
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  {/* Replace button */}
                  {onReplace && (
                    <button
                      onClick={() => onReplace(member.id, roleType)}
                      disabled={replacingId === member.id || removingId === member.id}
                      className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                      title="Replace team member"
                    >
                      {replacingId === member.id ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                      )}
                    </button>
                  )}
                  {/* Remove button */}
                  <button
                    onClick={() => onRemove(member.id)}
                    disabled={removingId === member.id || replacingId === member.id}
                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                    title="Remove from project"
                  >
                    {removingId === member.id ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamRoleSection;
